import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import theme from '../theme.config';
const BrandLogo = theme.assets.logo;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email and password are required');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      
      if (redirectTo) {
        navigate(redirectTo);
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'reseller') {
        navigate('/reseller/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-4 bg-[#09090b] text-zinc-50 relative">
      <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={BrandLogo} alt={theme.brand.name} className="h-10 w-auto object-contain" />
          </div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-mono text-[10px] text-emerald-400 font-semibold tracking-wider uppercase">Authentication Gate</span>
          </div>
          <h1 className="font-bold text-xl text-white">
            Log in to your account
          </h1>
          <p className="text-zinc-400 text-xs mt-1">
            Provide your credentials to access your console
          </p>
        </div>

        {/* Card */}
        <form onSubmit={handleSubmit}
          className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs text-zinc-400 font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="developer@neurovanta.io"
                className="bg-zinc-950 border-zinc-800 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs text-zinc-400 font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="bg-zinc-950 border-zinc-800 text-white pr-10 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-5 rounded-lg text-xs tracking-wider"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Authenticating...
                </span>
              ) : (
                <span>Log In</span>
              )}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-xs">
              Don't have an account yet?{' '}
              <Link to="/register" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-medium">
                Register a new account
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
